/**
 * Frame rate monitor + auto-adapt.
 * Watches rolling average FPS and emits events when sustained drops are detected.
 * Used to dynamically degrade quality on weak devices.
 */

type FrameCallback = (avgFps: number) => void;

export class FrameMonitor {
  private samples: number[] = [];
  private last = 0;
  private readonly windowSize: number;
  private readonly lowThreshold: number;
  private listeners: FrameCallback[] = [];
  private lowSustained = 0;
  private readonly sustainedFrames: number;

  constructor(options: { windowSize?: number; lowThreshold?: number; sustainedFrames?: number } = {}) {
    this.windowSize = options.windowSize ?? 60;
    this.lowThreshold = options.lowThreshold ?? 30;
    this.sustainedFrames = options.sustainedFrames ?? 120;
  }

  /** Call once per frame (e.g. inside the render loop). */
  tick(now: number): void {
    if (this.last === 0) {
      this.last = now;
      return;
    }
    const dt = now - this.last;
    this.last = now;
    if (dt <= 0) return;
    const fps = 1000 / dt;
    this.samples.push(fps);
    if (this.samples.length > this.windowSize) {
      this.samples.shift();
    }

    const avg = this.average();
    if (avg < this.lowThreshold) {
      this.lowSustained += 1;
      if (this.lowSustained >= this.sustainedFrames) {
        this.emit(avg);
        this.lowSustained = 0; // reset to avoid flooding
      }
    } else {
      this.lowSustained = 0;
    }
  }

  average(): number {
    if (this.samples.length === 0) return 60;
    let sum = 0;
    for (const s of this.samples) sum += s;
    return sum / this.samples.length;
  }

  onLowFps(cb: FrameCallback): () => void {
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb);
    };
  }

  private emit(avg: number) {
    for (const l of this.listeners) {
      try {
        l(avg);
      } catch (e) {
        console.error("FrameMonitor listener failed", e);
      }
    }
  }
}
